import React, { useState } from 'react';
import { DatePicker, Radio, Card, Spin } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { AiOutlineCalendar } from "react-icons/ai";
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import moment from 'moment';

import styled from 'styled-components';
import { DashboardWrapper } from './style';
import { GetGraphStats } from '../../slices/packing-stats';
// import { CalendarOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;

const CardWrapper = styled(Card)`
  padding: 0px;
  margin-top: 36px;
  border-color: #ebe5e5e6;
  .ant-card-body{
    padding: 8px;
  }
`;

const Charts = () => {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState('day');
  const [dateRange, setDateRange] = useState({
    start: dayjs().subtract(29, 'day').startOf('day').format('YYYY-MM-DD'),
    end: dayjs().endOf('day').format('YYYY-MM-DD')
  });

  const {
    fetchingGraphStats,
    graphStats,
  } = useSelector((state) => state.packingStats || {});

  const { prepStats, receivedStats } = graphStats || {};

  const chartOptions = {
    chart: {
      type: 'column',
      styledMode: false
    },
    title: {
      text: '',
      style: { 'text-align': 'left', 'display': 'none' }
    },
    xAxis: {
      categories: receivedStats?.map(({ label }) => label) || [],
    },
    yAxis: {
      // title: {
      //   text: 'Values'
      // }
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          inside: true,
          verticalAlign: 'middle',
          align: 'center',
          formatter: function () {
            return this.y || null;
          },
          style: {
            fontWeight: 'bold',
            color: '#fff',
            fontFamily: 'Titillium Web, sans-serif'
          }
        }
      }
    },
    series: [
      {
        name: 'Received',
        data: receivedStats?.map(({ totalItemUnits }) => totalItemUnits) || [],
        color: '#69c0ff'
      },
      {
        name: 'Prepped',
        data: prepStats?.map(({ total }) => total?.units_prepared) || [],
        color: '#b37feb'
      }
    ],
    legend: {
      layout: 'horizontal',
      align: 'right',
      verticalAlign: 'top',
      itemWidth: 100,
      itemDistance: 10,
    },
    tooltip: {
      useHTML: true,
      borderColor: '#ECEFF1',
      backgroundColor: '#fff',
      shadow: false,
      borderRadius: 8,
      padding: 8,
      formatter: function () {
        const index = this.point.index;
        const seriesName = this.series.name;

        const date = receivedStats?.[index]?.label || 'N/A';

        const { stats, totalItemUnits } = receivedStats?.[index] || 0;
        const { total, users } = prepStats?.[index] || 0;
        const { units_prepared } = total || {};

        if (seriesName === 'Received') {
          return `
            <div>
              <p style="font-size: 12px; margin: 0; font-weight: 600; text-align:center; color: #5253BE;">Received<p style="font-size:10px; margin: 0; margin-top: 8px; color:#808183; text-align:center;">${date}</p></p>
              <div>
                <p style="font-size: 12px; margin: 0; font-weight: 600; display:flex; justify-content: space-between; color: #1F1F1F;">TOTAL:<span style="font-size:10px; color:#4E5969;">${totalItemUnits}</span></p> 
              </div>
              ${stats?.map(({ full_name, item_units }) => (
            `<div>
              <p style="font-size: 12px; margin: 0; margin-top:4px; font-weight: 600; display:flex; justify-content: space-between; color: #1F1F1F;">${full_name}:<span style="font-size:10px; color:#4E5969;">${item_units}</span></p> 
            </div>`
          ))?.join('')}
            </div>
          `;
        }

        return (
          `<div>
            <p style="font-size: 12px; margin: 0; font-weight: 600; text-align:center; color: #5253BE;">Prepped<p style="font-size:10px; margin: 0; margin-top: 8px; color:#808183; text-align:center;">${date}</p></p>
            <div>
              <p style="font-size: 12px; margin: 0; font-weight: 600; display:flex; justify-content: space-between; color: #1F1F1F;">TOTAL:<span style="font-size:10px; color:#4E5969;">${units_prepared}</span></p> 
            </div>
            ${users?.map(({ full_name, units_prepared }) => (
            `<div>
              <p style="font-size: 12px; margin: 0; margin-top:4px; font-weight: 600; display:flex; justify-content: space-between; color: #1F1F1F;">${full_name}:<span style="font-size:10px; color:#4E5969;">${units_prepared}</span></p> 
            </div>`
          ))?.join('')}
          </div>`
        );
      }
    },
    credits: {
      enabled: false
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          legend: {
            layout: 'vertical',
            align: 'center',
            verticalAlign: 'bottom'
          }
        }
      }]
    }
  };

  const handleRangeChange = (dates) => {
    const start = dates[0]?.format("YYYY-MM-DD");
    const end = dates[1]?.format("YYYY-MM-DD");

    setDateRange({ start, end });
    dispatch(GetGraphStats({
      startDate: start,
      endDate: end,
      type: activeTab
    }));
  };

  const handleTabChange = (val) => {
    setActiveTab(val);
    dispatch(GetGraphStats({
      startDate: dateRange.start,
      endDate: dateRange.end,
      type: val
    }));
  };

  const { start, end } = dateRange || {};

  return (
    <Spin tip='Loading...' spinning={fetchingGraphStats}>
      <DashboardWrapper>
        <div className='container-overlay'>
          <h5>Overview</h5>
          <div className='right-container'>
            <div className='tabs-wrapper'>
              <p onClick={() => handleTabChange('day')} className={activeTab === 'day' ? 'active' : ''}>Day</p>
              <p onClick={() => handleTabChange('week')} className={activeTab === 'week' ? 'active' : ''}>Week</p>
              <p onClick={() => handleTabChange('month')} className={activeTab === 'month' ? 'active' : ''}>Month</p>
            </div>
            <RangePicker
              className='date-picker-custom-ui'
              prefix={<AiOutlineCalendar />}
              allowClear={false}
              style={{ width: '260px' }}
              value={[moment(start), moment(end)]}
              onChange={handleRangeChange}
            />
          </div>
        </div>
        <CardWrapper>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
          />
        </CardWrapper>
      </DashboardWrapper>
    </Spin>
  );
}

export default Charts;